// src/pages/PartnerCheckEmail.tsx
'use client';
import React from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import Button from "@/components/Button";
import ProgressBar from "@/components/ProgressBar";
import { getSupabase } from "@/lib/supabase";
import { ENV } from "@/lib/env";

// Helper to mask email
function maskEmail(e: string) {
  const [name, domain] = e.split("@");
  if (!domain) return e;
  const n = name.length <= 2 ? name[0] || "*" : `${name[0]}***${name[name.length - 1]}`;
  return `${n}@${domain}`;
}

export default function PartnerCheckEmail() {
  const [code, setCode] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);
  const [verifying, setVerifying] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const navigate = useNavigate();

  const email = localStorage.getItem("partner_signup_email") || "";

  // Redirect if no email
  React.useEffect(() => {
    if (!email) navigate("/partner/signup");
  }, [email, navigate]);

  // Cooldown timer
  React.useEffect(() => {
    if (cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Verify OTP
  const verify = async () => {
    setErr(null);
    if (code.length !== 6) {
      setErr("Enter the 6-digit code from your email.");
      return;
    }

    const supabase = getSupabase();
    if (!supabase) {
      setErr("App not configured.");
      return;
    }

    try {
      setVerifying(true);
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: "email",
      });

      if (error) throw error;

      if (data?.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });

        localStorage.removeItem("partner_signup_email");
        navigate("/partner/business-info", { replace: true });
      } else {
        throw new Error("Verification succeeded, but no session was returned.");
      }
    } catch (e: any) {
      setErr(e.message ?? "Invalid code. Try again."); // ← FIXED
    } finally {
      setVerifying(false);
    }
  };

  // Resend code
  const resend = async () => {
    setErr(null);
    if (cooldown > 0) return;

    try {
      const res = await fetch(`${ENV.EDGE_BASE}/auth-email-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${ENV.SUPABASE_ANON}`,
        },
        body: JSON.stringify({ email, partner: true }),
      });

      if (!res.ok) throw new Error("Failed to resend code");
      setCooldown(30);
    } catch (e: any) {
      setErr(e.message ?? "Failed to resend.");
    }
  };

  return (
    <AuthLayout>
      <div className="space-y-6 text-center">
        {/* HEADER */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0f172a]">
            Check your email
          </h1>
          <p className="text-sm sm:text-base text-gray-500 mt-2">
            We’ve sent a 6-digit confirmation code to{" "}
            <span className="text-[#0f172a] font-medium">
              {email ? maskEmail(email) : "your email"}
            </span>
            . The code expires shortly.
          </p>
        </div>

        {/* 6-DIGIT OTP INPUTS */}
        <div className="flex justify-center gap-3 my-6">
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const inputRef = React.useRef<HTMLInputElement>(null);
            return (
              <input
                key={i}
                ref={inputRef}
                type="text"
                inputMode="numeric"
                pattern="\d"
                maxLength={1}
                value={code[i] ?? ""}
                className={`
                  w-12 h-14 bg-gray-800 border border-gray-700 rounded-xl
                  text-2xl font-semibold text-[#0f172a] text-center
                  focus:outline-none focus:border-[#a78bfa] focus:ring-2 focus:ring-[#a78bfa]/30
                  transition-all duration-200
                `}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  if (!val) return;
                  const digits = code.split("");
                  digits[i] = val;
                  const next = digits.join("").slice(0, 6);
                  setCode(next);
                  if (i < 5 && val) {
                    const nextInput = inputRef.current?.nextElementSibling as HTMLInputElement | null;
                    nextInput?.focus();
                  }
                  if (next.length === 6) verify();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Backspace" && !code[i]) {
                    const prev = inputRef.current?.previousElementSibling as HTMLInputElement | null;
                    prev?.focus();
                    prev?.select();
                  }
                  if (e.key === "Enter" && code.length === 6) verify();
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                  if (paste.length > 0) {
                    setCode(paste.padEnd(6, " ").slice(0, 6));
                    setTimeout(() => verify(), 100);
                  }
                }}
                onFocus={(e) => e.target.select()}
              />
            );
          })}
        </div>

        {/* VERIFY BUTTON */}
        <Button
          onClick={verify}
          disabled={verifying}
          className={`
            w-full bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-semibold
            rounded-2xl py-3 transition-all duration-200
            active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed
          `}
        >
          {verifying ? "Verifying..." : "Verify code"}
        </Button>

        {/* RESEND + CHANGE EMAIL */}
        <div className="flex justify-center gap-4 items-center text-sm">
          <Button
            onClick={resend}
            disabled={cooldown > 0}
            variant="secondary"
            className="text-gray-500 hover:text-[#0f172a]"
          >
            {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
          </Button>
          <button
            onClick={() => navigate("/partner/signup")}
            className="text-gray-500 hover:text-[#0f172a] transition"
          >
            Change email
          </button>
        </div>

        {/* ERROR */}
        {err && <p className="text-sm text-red-400">{err}</p>}

        {/* FOOTER LINKS */}
        <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500 mt-6">
          <a href="#" className="hover:text-[#0f172a] transition">Terms</a>
          <a href="#" className="hover:text-[#0f172a] transition">Privacy</a>
          <a href="#" className="hover:text-[#0f172a] transition">Cookies</a>
          <a href="#" className="hover:text-[#0f172a] transition">Contact</a>
        </div>

        <div className="my-6 border-t border-gray-800" />

        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <span className="w-2 h-2 bg-[#a78bfa] rounded-full" />
          Input
        </div>

        <footer className="text-center text-xs text-gray-500">
          © 2025 Input. All rights reserved.
        </footer>

        {/* PROGRESS BAR */}
        <div className="mt-6">
          <ProgressBar step={1} total={3} />
        </div>
      </div>
    </AuthLayout>
  );
}
